import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wheat,
  Search,
  SlidersHorizontal,
  X,
  List,
  Map as MapIcon,
  Info,
  SearchX,
  FilterX,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/button/button';
import { FilterChip } from '@/components/filterChip/filterChip';
import { ListingCard } from '@/components/listingCard/listingCard';
import { LoadMore } from '@/components/loadMore/loadMore';
import { PhotoFallback } from '@/components/photoFallback/photoFallback';
import { SearchSelect } from '@/components/searchSelect/searchSelect';
import { Toast } from '@/components/toast/toast';
import { categoryChipIcon, categoryChipLabel, productMenuItems } from './productMenu';
import { hasActiveFilters } from './searchScope';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useListingIcons } from '@/hooks/useListingIcons';
import { UserMenu } from '@/components/userMenu/userMenu';
import { useAuth } from '@/auth/authContext';
import { locationKey } from '@/components/locationCascade/locationSelection';
import {
  getCategories,
  getFavoriteIds,
  getListing,
  getListingExtent,
  getListings,
  getProducts,
  getSearchSuggestions,
  toggleFavoriteListing,
  type ListingFilters,
  type SearchSuggestions,
} from '@/services/listingService';
import { tr } from '@/i18n/tr';
import type { Category, Listing, Producer, Product } from '@/types/domain';
import { EmptyState } from '@/components/emptyState/emptyState';
import { FilterSheet, type SheetFilters } from './filterSheet';
import { ListingMap, type MapFocus } from './listingMap';
import { SearchSuggestionsPanel } from './searchSuggestions';
import styles from './explore.module.scss';
import { formatNumber, formatPrice } from '@/util/number';
import { formatLocation } from '@/util/formatLocation';
import { onHorizontalWheel } from '@/util/horizontalWheel';
import { TURKEY_BBOX } from '@/util/locationGeo';
import { paddedExtent } from '@/util/extent';
import { filterByQuery } from '@/util/turkishSearch';
import { ProduceIcon } from '@/components/produceIcon/produceIcon';
import { iconSourceOf } from '@/components/produceIcon/produceArt';

/** Long enough to swallow a whole word at typing speed, short enough to still feel instant. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * A page is whole rows of the grid, not a fixed count.
 *
 * The grid's column count follows its own width, so one number cannot serve
 * both ends: twelve cards is three screens on a phone and half a screen at five
 * columns, where "Daha fazla yükle" lands before the reader has scrolled at all.
 * Measuring the columns and asking for this many rows keeps the button roughly
 * the same distance down the page at every width.
 */
const LISTING_ROWS_PER_PAGE = 8;

/** Floor for a single-column phone, where 8 rows is a short page. */
const MIN_LISTINGS_PAGE_SIZE = 15;

/** Sebzeler runs to 33 products, and a menu that long covers the map it filters. */
const PRODUCT_MENU_ROWS = 6;

/** Subcategory rows in the search panel, before the listings they sit above. */
const SUGGESTED_PRODUCT_ROWS = 5;

export interface ExploreScreenProps {
  /** "İlan Ver" nudge in the empty-result state (App switches to the create tab). */
  onCreateListing?: () => void;
}

/**
 * Explore ("Keşfet") — map-first discovery screen.
 * Map is the default view; "Liste Göster" toggles the same filtered result set
 * into a card list. Filters are shared by both views.
 */
export function ExploreScreen({ onCreateListing }: ExploreScreenProps) {
  const navigate = useNavigate();
  const { status: authStatus } = useAuth();
  const iconFor = useListingIcons();
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [listings, setListings] = useState<Listing[]>([]);
  const [listingsTotal, setListingsTotal] = useState(0);
  const [loadingMoreListings, setLoadingMoreListings] = useState(false);
  const listingsGenRef = useRef(0);
  // An extent answer that arrives after the user has picked a different product
  // would fly the map to the wrong place; the generation makes the late one a
  // no-op, exactly as listingsGenRef does for the card page.
  const extentGenRef = useRef(0);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const [categoryId, setCategoryId] = useState<string>();
  const [productId, setProductId] = useState<string>();
  const [query, setQuery] = useState('');
  // The box updates on every keystroke; the two requests below wait for the
  // typing to stop, so "domates" costs one round trip instead of seven.
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
  // Everything the filter drawer owns: location, status and price.
  const [filters, setFilters] = useState<SheetFilters>({});
  const [producerFilter, setProducerFilter] = useState<Producer>();

  const gridRef = useRef<HTMLDivElement>(null);
  const [pageSize, setPageSize] = useState(MIN_LISTINGS_PAGE_SIZE);

  const [view, setView] = useState<'map' | 'list'>('map');
  const [selected, setSelected] = useState<Listing>();
  const [mapFocus, setMapFocus] = useState<MapFocus>();
  const [mapHintDismissed, setMapHintDismissed] = useState(false);

  // An empty result is announced, not drawn: a card on the map covers the very
  // thing being filtered, and has to be closed by hand before the map is usable.
  //
  // Two different messages share the one slot: "nothing matched" is a dead end,
  // "filters cleared" is a thing that just worked. The kind is what tells the
  // two apart, in tone and icon.
  const [toast, setToast] = useState<{ message: string; kind: 'empty' | 'cleared' } | null>(null);

  const [filterOpen, setFilterOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<SearchSuggestions>();
  const [suggestOpen, setSuggestOpen] = useState(false);

  useEffect(() => {
    getCategories().then(setCategories);
    getProducts().then(setProducts);
  }, []);

  useEffect(() => {
    if (authStatus !== 'authed') {
      setFavoriteIds(new Set());
      return;
    }
    let alive = true;
    getFavoriteIds().then(({ listingIds }) => {
      if (alive) setFavoriteIds(new Set(listingIds));
    });
    return () => {
      alive = false;
    };
  }, [authStatus]);

  const toggleFavorite = (listing: Listing) => {
    if (authStatus !== 'authed') {
      navigate('/login');
      return;
    }
    const wasFavorite = favoriteIds.has(listing.id);
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (wasFavorite) next.delete(listing.id);
      else next.add(listing.id);
      return next;
    });
    toggleFavoriteListing(listing.id).catch(() => {
      // revert on failure
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (wasFavorite) next.add(listing.id);
        else next.delete(listing.id);
        return next;
      });
    });
  };

  /**
   * One filter object for both surfaces. The list asks for cards and the map
   * asks for coordinates, but they must agree on WHICH listings — building the
   * argument twice is how the two drift.
   */
  const activeFilters = useMemo(() => {
    // The whole selection is the drawer's state; the query only takes the three
    // fields the server matches on.
    const { location, ...sheet } = filters;
    return {
      categoryId,
      productId,
      // While a producer is selected the box holds their name for display only;
      // sending it as `q` would match it against listing titles and empty the
      // results. Editing the box clears the producer, and the text becomes a
      // real query again.
      query: producerFilter ? '' : debouncedQuery,
      producerId: producerFilter?.id,
      ...sheet,
      city: location?.city,
      district: location?.district,
      neighbourhoodId: location?.neighbourhoodId,
    };
  }, [categoryId, productId, debouncedQuery, producerFilter, filters]);

  useEffect(() => {
    let alive = true;
    const gen = ++listingsGenRef.current;
    setLoading(true);
    getListings(activeFilters, 0, pageSize).then(({ items, total }) => {
      if (!alive || gen !== listingsGenRef.current) return;
      setListings(items);
      setListingsTotal(total);
      setLoading(false);
      setSelected((s) => (s && !items.some((l) => l.id === s.id) ? undefined : s));
      // Per completed search, not per keystroke: the query is debounced, so this
      // says "nothing matched" once instead of flickering as the user types.
      if (items.length === 0) {
        const message = hasActiveFilters(activeFilters)
          ? tr.explore.emptyToast
          : tr.explore.noListingsTitle;
        setToast({ message, kind: 'empty' });
      }
    });
    return () => {
      alive = false;
    };
  }, [activeFilters, pageSize]);

  /**
   * How many cards make a page, read off the grid the browser actually laid out.
   *
   * getComputedStyle resolves the auto-fill track list to real tracks, so this
   * is the column count on screen rather than one inferred from a breakpoint —
   * the same reason the grid itself is not keyed to the viewport.
   */
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;

    const measure = () => {
      const columns =
        getComputedStyle(el).gridTemplateColumns.split(' ').filter(Boolean).length || 1;
      const next = Math.max(MIN_LISTINGS_PAGE_SIZE, columns * LISTING_ROWS_PER_PAGE);
      // Same value must not re-set state: this runs on every resize frame, and
      // the page size sits in the fetch effect's dependencies.
      setPageSize((prev) => (next === prev ? prev : next));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
    // listings.length, not showEmpty: the grid only exists once there are cards
    // to lay out, so this re-attaches when it mounts.
  }, [view, listings.length]);

  /**
   * LoadMore fetches the next page from the server and appends it — the list
   * is the whole public catalogue (unbounded), unlike the small per-user lists
   * useIncrementalList is for. `gen` guards against a page that arrives after
   * the filters moved on (same race the search suggestions load-more guards).
   */
  const showMoreListings = async () => {
    if (loadingMoreListings) return;
    const gen = listingsGenRef.current;
    setLoadingMoreListings(true);
    try {
      const { items, total } = await getListings(activeFilters, listings.length, pageSize);
      if (gen !== listingsGenRef.current) return;
      setListings((prev) => [...prev, ...items]);
      setListingsTotal(total);
    } finally {
      if (gen === listingsGenRef.current) setLoadingMoreListings(false);
    }
  };

  /**
   * A map pin carries an id, not a card — the map is fed thin points now. Use
   * the loaded list when the pin is one of the visible results, and fall back to
   * fetching it, which is what happens for a pin outside the current page.
   */
  const selectFromMap = (id: string | undefined) => {
    if (!id) {
      setSelected(undefined);
      return;
    }
    const loaded = listings.find((l) => l.id === id);
    if (loaded) {
      setSelected(loaded);
      return;
    }
    setSelected(undefined);
    getListing(id)
      .then((detail) => {
        setSelected((s) => (s === undefined && detail ? detail.listing : s));
      })
      .catch(() => undefined);
  };

  // autocomplete: listings + producers once the query has some substance.
  // The server pages; each "Daha fazla yükle" fetches the next ten and appends,
  // so the panel accumulates rather than replacing.
  const [suggestBusy, setSuggestBusy] = useState(false);

  useEffect(() => {
    const q = debouncedQuery.trim();
    if (q.length < 2) {
      setSuggestions(undefined);
      return;
    }
    let alive = true;
    getSearchSuggestions(q).then((s) => {
      if (alive) setSuggestions(s);
    });
    return () => {
      alive = false;
    };
  }, [debouncedQuery]);

  const loadMoreSuggestions = async () => {
    if (!suggestions || suggestBusy) return;
    const q = debouncedQuery.trim();
    setSuggestBusy(true);
    try {
      const next = await getSearchSuggestions(q, suggestions.listings.length);
      // Guard against a page that arrives after the query moved on: appending it
      // would mix two searches in one list.
      if (q !== debouncedQuery.trim()) return;
      // Keep the producers already held: they are unpaged and only come back
      // with the first page, so spreading `next` wholesale would empty them.
      setSuggestions((prev) =>
        prev
          ? { ...next, producers: prev.producers, listings: [...prev.listings, ...next.listings] }
          : next
      );
    } finally {
      setSuggestBusy(false);
    }
  };

  const chipsRef = useRef<HTMLDivElement>(null);
  /** Positioning host for the product menu — see the anchorTo prop it is passed. */
  const controlsRef = useRef<HTMLDivElement>(null);
  const [chipEdges, setChipEdges] = useState({ start: false, end: false });
  /**
   * Whether the next category selection should open its menu straight away.
   *
   * True when the reader picked the category by pressing its chip: the pill's
   * chevron is a thin hint on its own, and picking a category used to make a
   * labelled row appear, so something has to say the subcategories are there.
   * False when the category was set for them — choosing a product from the
   * search panel already answers the question the menu would ask.
   */
  const [menuAutoOpen, setMenuAutoOpen] = useState(false);

  /**
   * A category can sit far along the scroll row, and its menu hangs off its
   * chip — which has to be on screen for that to read.
   *
   * Skipped when the reader picked the category by pressing its chip: that chip
   * is already in view, and scrolling the row under a menu that is opening
   * dismisses it. The menu drops its anchor on any scroll of the row, so the
   * smooth scroll here and the open raced — invisibly on desktop, where all
   * seven chips fit and no scroll happens, and every time at phone width.
   */
  useEffect(() => {
    if (!categoryId || menuAutoOpen) return;
    chipsRef.current
      ?.querySelector<HTMLElement>(`[data-category="${categoryId}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }, [categoryId, menuAutoOpen]);

  // Which end the row still continues past — drives the fade that tells the
  // user it swipes. Re-measured when the chip set changes, not just on scroll.
  useEffect(() => {
    const el = chipsRef.current;
    if (!el) return;
    const measure = () => {
      const max = el.scrollWidth - el.clientWidth;
      setChipEdges({ start: el.scrollLeft > 2, end: el.scrollLeft < max - 2 });
    };
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [categories, categoryId, producerFilter]);

  /**
   * Selecting a category inserts the product menu straight after its chip,
   * which on a phone is usually past the right edge of the row: the reader taps
   * a category and the control they are meant to reach for next is off screen,
   * with its menu anchored under a trigger they cannot see. Bring it into view.
   *
   * scrollBy on the row rather than scrollIntoView, which also scrolls every
   * scrollable ancestor — including the page — to reach the element.
   */
  useEffect(() => {
    const el = chipsRef.current;
    if (!el || !categoryId) return;
    const trigger = el.querySelector<HTMLElement>('[aria-haspopup="listbox"]');
    if (!trigger) return;
    const row = el.getBoundingClientRect();
    const rect = trigger.getBoundingClientRect();
    const past = rect.right - row.right;
    const before = row.left - rect.left;
    // A margin past the edge, so the chip lands inside the row rather than
    // flush against the fade mask that covers its last 28px.
    if (past > 0) el.scrollBy({ left: past + 32, behavior: 'smooth' });
    else if (before > 0) el.scrollBy({ left: -(before + 32), behavior: 'smooth' });
  }, [categoryId]);

  /**
   * Categories matching the typed query, resolved here rather than on the
   * server: the full list is already loaded for the chip row, and filterByQuery
   * folds Turkish case the way the rest of the app's search boxes do (so "cilek"
   * finds "Çilek").
   */
  const suggestedCategories = useMemo(
    () => (debouncedQuery.trim().length >= 2 ? filterByQuery(categories, debouncedQuery) : []),
    [categories, debouncedQuery]
  );

  /**
   * Subcategories matching the query, from the same locally held list the menu
   * is built from. Capped: Sebzeler alone has 33 products, and a query like "bi"
   * would otherwise bury the listings under a wall of product names.
   */
  const suggestedProducts = useMemo(
    () =>
      debouncedQuery.trim().length >= 2
        ? filterByQuery(products, debouncedQuery).slice(0, SUGGESTED_PRODUCT_ROWS)
        : [],
    [products, debouncedQuery]
  );

  const categoryOf = (l: Listing) => categories.find((c) => c.id === l.categoryId);
  const activeFilterCount = useMemo(
    () => Object.values(filters).filter((v) => v !== undefined).length,
    [filters]
  );

  /**
   * Search is deliberately a master search: it matches the whole catalogue
   * regardless of what is filtered. That leaves one problem: flying to a result
   * the active filters exclude shows its card with no pin under it, so the map
   * appears to have lost the very listing it was asked for. Clearing the filters
   * is what makes the pin exist. The typed query stays — the result matches it
   * by definition, so it can never hide its own pin.
   *
   * Suggestion rows are thin (anti-scrape: no price, quantity or photos —
   * see toSearchListing), so they can't feed the preview card as-is or it
   * renders "0 TL/kg". Prefer the full row already loaded for the map, and fall
   * back to fetching the detail before the card is painted.
   */
  const openListingOnMap = (l: Listing) => {
    setView('map');
    setSuggestOpen(false);

    const hadFilters =
      Boolean(categoryId) || Boolean(productId) || Boolean(producerFilter) || activeFilterCount > 0;
    if (hadFilters) {
      setCategoryId(undefined);
      setProductId(undefined);
      setProducerFilter(undefined);
      setFilters({});
      // Dropping someone's price and location filters silently is worse than
      // the missing pin was; say so.
      setToast({ message: tr.explore.filtersClearedToast, kind: 'cleared' });
    }

    setMapFocus({ lon: l.coords.lon, lat: l.coords.lat, zoom: 11 });

    const loaded = listings.find((x) => x.id === l.id);
    if (loaded) {
      setSelected(loaded);
      return;
    }
    setSelected(undefined);
    getListing(l.id).then((detail) => {
      // ignore a stale response if the user has picked something else since
      setSelected((s) => (s === undefined && detail ? detail.listing : s));
    });
  };

  const productItems = useMemo(
    () =>
      categoryId
        ? products
            .filter((p) => p.categoryId === categoryId)
            .map((p) => ({ id: p.id, name: p.name, icon: <ProduceIcon icon={iconSourceOf(p)} /> }))
        : [],
    [products, categoryId]
  );

  /** What the selected category's chip shows instead of its own name. */
  const selectedProduct = productItems.find((p) => p.id === productId);

  /**
   * Products belong to exactly one category, so a category change always
   * invalidates the chosen product — clearing it here is what stops a filter
   * that is no longer on screen from silently narrowing the results.
   */
  /**
   * Searching and filtering are two ways of asking the same question, so the
   * app commits to one at a time: choosing any filter drops the typed query,
   * just as opening a search result drops the filters. Leaving both on is what
   * produced "no results" for a term that plainly matches something.
   */
  const clearSearch = () => {
    setQuery('');
    setSuggestOpen(false);
  };

  const pickCategory = (id?: string) => {
    setCategoryId(id);
    setProductId(undefined);
    clearSearch();
  };

  /**
   * Picking from the search reframes the map onto every listing that matches,
   * wherever they are.
   *
   * Someone zoomed into Istanbul who filters for Elma should not be told there
   * are none when there is one in Trabzon and one in Ankara — and "where is
   * this" cannot be answered by a viewport-bounded query, which is why the
   * extent is asked for separately. The existing focus/fit path does the flying,
   * so a whole-country spread and a single village get the same treatment.
   *
   * Only picking moves the camera. Clearing the selection leaves the reader
   * where they are: they were narrowing a view they had already chosen.
   */
  const fitMapToFilters = (filters: ListingFilters) => {
    const gen = ++extentGenRef.current;
    getListingExtent(filters)
      .then(({ bbox }) => {
        if (gen !== extentGenRef.current || !bbox) return;
        setMapFocus({ extent: paddedExtent(bbox) });
      })
      // A failed reframe leaves the map where it is; the result list still
      // updates, so there is nothing here worth interrupting the user for.
      .catch(() => undefined);
  };

  const fitMapToSelection = (next: Partial<ListingFilters>) =>
    // query: '' because clearSearch has just dropped it — activeFilters still
    // carries the old text this render, and framing the map to a query on its
    // way out would fit the wrong set of listings.
    fitMapToFilters({ ...activeFilters, query: '', ...next });

  /**
   * The map is unmounted while the list is up, so coming back rebuilds its view
   * from the stored position — which is wherever the reader last panned, not
   * where the results they have just been reading are. With one hit in the east
   * and one in the west that lands them in the middle of the country with
   * neither in sight.
   *
   * Only a filtered switch reframes. Unfiltered, the stored position is the
   * right answer: it is the view they chose, and the whole catalogue is not a
   * place to fly to.
   */
  const showMap = () => {
    setView('map');
    if (hasActiveFilters(activeFilters)) fitMapToFilters(activeFilters);
  };

  const pickProduct = (id?: string) => {
    setProductId(id);
    clearSearch();
    if (id) fitMapToSelection({ productId: id });
  };

  /**
   * A subcategory chosen from the search panel rather than the menu. It carries
   * its own category, which has to be selected too — the menu below the chips
   * lists one category's products, so a product without its category selected
   * would filter to something the controls do not show.
   */
  const pickProductSuggestion = (p: Product) => {
    // The category is being set on the reader's behalf, and the product with
    // it — popping its menu open would offer a choice already made.
    setMenuAutoOpen(false);
    setCategoryId(p.categoryId);
    setProductId(p.id);
    clearSearch();
    fitMapToSelection({ categoryId: p.categoryId, productId: p.id });
  };

  /**
   * A producer filters the results without a chip of its own: their name stays
   * in the search box, which is the visible statement of what is being shown.
   *
   * The name is left in the box as a LABEL, not a query — activeFilters drops
   * the text while a producer is selected, because sending it as `q` would
   * match it against listing titles and return nothing.
   */
  const pickProducer = (p: Producer) => {
    setProducerFilter(p);
    setQuery(p.displayName);
    setSuggestOpen(false);
    // Their listings are wherever their fields are, which is rarely the part of
    // the country on screen. Without this the filter applies to a viewport that
    // holds none of it, and picking a producer reads as doing nothing.
    fitMapToSelection({ producerId: p.id });
  };

  /**
   * The drawer is the only surface that edits location, so applying it is where
   * the camera follows the selection — framing whatever was just picked, or the
   * whole country once the location is cleared.
   *
   * Only a change of place moves the camera. Narrowing the price of listings in
   * a valley the buyer has panned to must leave them in that valley.
   */
  const applyFilters = (next: SheetFilters) => {
    if (locationKey(filters.location) !== locationKey(next.location)) {
      setMapFocus({ extent: next.location?.bbox ?? TURKEY_BBOX });
    }
    setFilters(next);
    clearSearch();
  };

  const showEmpty = !loading && listings.length === 0;
  const isFiltered = hasActiveFilters(activeFilters);
  const remainingListings = listingsTotal - listings.length;

  const viewToggle = (floating: boolean) => (
    <FilterChip
      className={`${styles.viewToggle} ${floating ? styles.viewToggleFloating : ''}`}
      icon={view === 'map' ? <List /> : <MapIcon />}
      onClick={() => (view === 'map' ? setView('list') : showMap())}
    >
      {view === 'map' ? tr.explore.showList : tr.explore.showMap}
    </FilterChip>
  );

  return (
    <div className={styles.screen}>
      <header className={styles.header}>
        <div className={styles.brandRow}>
          <div className={styles.brandBlock}>
            <span className={styles.logo}>
              <Wheat />
            </span>
            <strong>{tr.app.name}</strong>
          </div>
          <UserMenu />
        </div>

        <div
          className={styles.searchArea}
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setSuggestOpen(false);
          }}
        >
          <div className={styles.searchRow}>
            <div className={styles.searchField}>
              <Search aria-hidden />
              <input
                type="search"
                placeholder={tr.explore.searchPlaceholder}
                value={query}
                onFocus={() => setSuggestOpen(true)}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSuggestOpen(true);
                  // Typing over a producer's name means a new search, not an
                  // edit of the label — so the producer filter it stood for goes
                  // with it. This is also the only way to clear that filter,
                  // now that it has no chip with an X on it.
                  setProducerFilter(undefined);
                }}
              />
              {query.length > 0 && (
                <button
                  type="button"
                  className={styles.searchClear}
                  aria-label={tr.explore.searchClear}
                  // Keeps focus in the field, so clearing does not blur the row
                  // and collapse the panel under the pointer.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setQuery('');
                    setProducerFilter(undefined);
                    setSuggestOpen(false);
                  }}
                >
                  <X aria-hidden />
                </button>
              )}
            </div>
            <button
              type="button"
              className={styles.filterButton}
              aria-label={tr.explore.filters.title}
              onClick={() => setFilterOpen(true)}
            >
              <SlidersHorizontal />
              {activeFilterCount > 0 && (
                <span className={styles.filterCount}>{activeFilterCount}</span>
              )}
            </button>
          </div>

          {/* Categories match from two characters, listings and producers from
              three (the server's floor), so the panel can have something to
              show before the request is even made. */}
          {suggestOpen &&
            query.trim().length >= 2 &&
            (suggestions || suggestedCategories.length > 0 || suggestedProducts.length > 0) && (
              <SearchSuggestionsPanel
                data={suggestions ?? { listings: [], producers: [], total: 0 }}
                categories={suggestedCategories}
                products={suggestedProducts}
                busy={suggestBusy}
                onLoadMore={loadMoreSuggestions}
                onPickListing={openListingOnMap}
                onPickProducer={pickProducer}
                onPickCategory={(c) => pickCategory(c.id)}
                onPickProduct={pickProductSuggestion}
              />
            )}
        </div>
      </header>

      {/* Positioned, so the product menu portaled here lands against this row
          rather than the page. */}
      <div className={styles.controls} ref={controlsRef}>
        <div
          className={`${styles.chips} ${chipEdges.start ? styles.fadeStart : ''} ${
            chipEdges.end ? styles.fadeEnd : ''
          }`}
          ref={chipsRef}
          onWheel={onHorizontalWheel}
        >
          {/* No chip for the selected producer: their name sits in the search
              box instead, which is where the reader just typed and so the place
              they look to see what is being shown. */}
          <FilterChip selected={!categoryId} onClick={() => pickCategory(undefined)}>
            {tr.common.all}
          </FilterChip>
          {/* The selected category's chip IS its subcategory menu's trigger.
              A menu rather than more chips: 33 products in the row would push
              the category itself off screen, and typing beats scrolling to
              reach one. Nothing is added below the chips, so the band above
              the map never grows — and the control the reader opens is the
              same pill they just pressed, rather than a second one beside it. */}
          {categories.map((c) => {
            const isSelected = categoryId === c.id;
            const menuItems = isSelected ? productMenuItems(productItems) : [];

            // A category with no products of its own keeps the plain toggle:
            // there is no menu to open, so tapping it must still turn it off.
            if (!isSelected || menuItems.length === 0) {
              return (
                <FilterChip
                  key={c.id}
                  data-category={c.id}
                  icon={<ProduceIcon icon={iconSourceOf(c)} />}
                  selected={isSelected}
                  onClick={() => {
                    setMenuAutoOpen(!isSelected);
                    pickCategory(isSelected ? undefined : c.id);
                  }}
                >
                  {c.name}
                </FilterChip>
              );
            }

            return (
              <SearchSelect
                key={c.id}
                ariaLabel={tr.explore.productLabel}
                items={menuItems}
                value={productId ?? ''}
                onChange={(id) => pickProduct(id || undefined)}
                placeholder={tr.explore.productPlaceholder}
                anchorTo={controlsRef}
                searchable
                initialCount={PRODUCT_MENU_ROWS}
                defaultOpen={menuAutoOpen}
                renderTrigger={(trigger) => (
                  <FilterChip
                    {...trigger}
                    data-category={c.id}
                    icon={
                      <ProduceIcon
                        icon={iconSourceOf(
                          categoryChipIcon(
                            c,
                            products.find((p) => p.id === productId && p.categoryId === c.id)
                          )
                        )}
                      />
                    }
                    selected
                    // It no longer toggles — tapping opens the menu, and the
                    // "Tümü" chip is what turns the category off. Claiming
                    // "pressed" as well as "expanded" would describe a control
                    // that does two things on one tap.
                    aria-pressed={undefined}
                  >
                    {categoryChipLabel(c, selectedProduct)}
                  </FilterChip>
                )}
              />
            );
          })}
        </div>
      </div>

      {/* Both views start here, so one toast serves either. */}
      <div className={styles.toastAnchor}>
        <Toast
          message={toast?.message ?? null}
          onDismiss={() => setToast(null)}
          icon={toast?.kind === 'cleared' ? <FilterX aria-hidden /> : <SearchX aria-hidden />}
          tone={toast?.kind === 'cleared' ? 'success' : 'warning'}
          placement="inline"
        />
      </div>

      {view === 'map' ? (
        <div className={styles.mapArea}>
          <ListingMap
            filters={activeFilters}
            selectedId={selected?.id}
            onSelectId={selectFromMap}
            focus={mapFocus}
            countyCode={filters.location?.countyCode}
            townId={filters.location?.townId}
            neighbourhoodId={filters.location?.neighbourhoodId}
          />

          {viewToggle(true)}

          {!showEmpty && !selected && !mapHintDismissed && (
            <aside className={styles.mapHint}>
              <Info aria-hidden />
              <p>{tr.explore.mapHint}</p>
              <button
                type="button"
                onClick={() => setMapHintDismissed(true)}
                aria-label={tr.common.close}
              >
                <X />
              </button>
            </aside>
          )}

          {selected && (
            <aside className={styles.preview}>
              <div className={styles.previewHead}>
                {/* Name only: the emoji on this card now means the product, so a
                    second one meaning the category reads as a mismatch. */}
                {categoryOf(selected) && (
                  <span className={styles.previewCategory}>{categoryOf(selected)!.name}</span>
                )}
                <span className={styles.previewLocation}>
                  <MapPin /> {formatLocation(selected, { short: true })}
                  {selected.locationPrecision === 'approximate' && (
                    <span className={styles.approximateBadge}>
                      {tr.createListing.locationApproximate}
                    </span>
                  )}
                </span>
              </div>
              <div className={styles.previewBody}>
                {selected.photos[0] ? (
                  <img src={selected.photos[0].url} alt={selected.photos[0].alt} />
                ) : (
                  <PhotoFallback className={styles.rowThumb} icon={iconFor(selected)} />
                )}
                <div className={styles.previewInfo}>
                  <h3>{selected.title}</h3>
                  <p>
                    <ProduceIcon icon={iconFor(selected)} />{' '}
                    {selected.productName || selected.productType}
                  </p>
                  <p className={styles.previewPrice}>
                    {formatPrice(selected.price)} <em>TL/{selected.priceUnit}</em>
                    <span className={styles.previewQuantity}>
                      {tr.common.quantity}: {formatNumber(selected.quantity)}{' '}
                      {selected.quantityUnit}
                    </span>
                  </p>
                </div>
              </div>
              <div className={styles.previewFooter}>
                {selected.distanceKm !== undefined &&
                  selected.locationPrecision !== 'approximate' && (
                    <span>{tr.common.distanceAway(String(selected.distanceKm))}</span>
                  )}
                <Button onClick={() => navigate(`/listing/${selected.id}`)}>
                  {tr.common.goToDetail} →
                </Button>
              </div>
            </aside>
          )}
        </div>
      ) : (
        <div className={styles.listArea}>
          <div className={styles.listHead}>
            <p className={styles.resultCount}>{tr.explore.resultCount(listings.length)}</p>
            {viewToggle(false)}
          </div>
          {showEmpty ? (
            <div className={styles.emptyWrap}>
              <EmptyState
                icon={SearchX}
                title={isFiltered ? tr.explore.emptyTitle : tr.explore.noListingsTitle}
                body={isFiltered ? tr.explore.emptyHint : tr.explore.noListingsHint}
                action={
                  onCreateListing && { label: tr.nav.createListing, onClick: onCreateListing }
                }
              />
            </div>
          ) : (
            <>
              <div className={styles.grid} ref={gridRef}>
                {listings.map((l) => (
                  <ListingCard
                    key={l.id}
                    listing={{ ...l, isFavorite: favoriteIds.has(l.id) }}
                    category={categoryOf(l)}
                    onOpen={() => navigate(`/listing/${l.id}`)}
                    onToggleFavorite={toggleFavorite}
                  />
                ))}
              </div>
              <LoadMore remaining={remainingListings} onClick={showMoreListings} />
            </>
          )}
        </div>
      )}

      <FilterSheet
        open={filterOpen}
        value={filters}
        onApply={applyFilters}
        onClose={() => setFilterOpen(false)}
      />
    </div>
  );
}
